import {
  Alert,
  Card,
  GKButton,
  GKDatePicker,
  Grid2,
  Popover,
  SimpleLoader,
  Typography,
} from "components";
import GKCharts from "components/GKCharts";
import dayjs from "dayjs";
import { useFetchQuery } from "hooks/useQueries";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { PORTFOLIO_COMPARISION } from "utils/apiRoutes";
import { formatYAxisLabel } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import BenchmarkTable from "./BenchmarkTable";
import SelectBenchmarkModal from "./SelectBenchmarkModal";

interface ChartData {
  data: unknown;
  name: string;
  marker: { enabled: boolean };
  color?: string;
  borderColor?: string;
}

const Benchmark = ({ benchmarkId }: { benchmarkId?: string }) => {
  useDocumentTitle("Portfolio Benchmark");

  const { id } = useParams();

  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);
  const [chartData, setChartData] = useState<ChartData[]>();
  const [dateRange, setDateRange] = useState("monthToDate");
  // 2. Yeh 3 states add karo
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [calendarAnchor, setCalendarAnchor] =
    useState<HTMLButtonElement | null>(null);

  const { isFetching, data: basketChartTableData } = useFetchQuery({
    key: ["GET_ALL_BENCHMARK_CHART", benchmarkId],
    route: PORTFOLIO_COMPARISION(id, benchmarkId),
    success: (data: any) =>
      setChartData(
        Object.entries(data?.data)?.map(([key, val]) => {
          return {
            data: val,
            name: key,
            marker: { enabled: false },
          };
        }),
      ),
  });

  if (isFetching) return <SimpleLoader />;

  // 3. Custom filter function — sinceInception se date filter karega
  const getCustomFilteredSeries = () => {
    console.log(chartData);
    const sinceInceptionData = chartData?.find(
      (d) => d.name === "sinceInception",
    );
    if (!sinceInceptionData || !customStartDate || !customEndDate) return [];

    const startTs = new Date(customStartDate).getTime();
    const endTs = new Date(customEndDate).getTime();

    const cal = Object.keys((sinceInceptionData as any).data).map((key) => ({
      key,
      data: (sinceInceptionData as any).data[key].filter(
        (item: any) => item[0] >= startTs && item[0] <= endTs,
      ),
    }));

    const result = Object.keys((sinceInceptionData as any).data).map(
      (key, index) => ({
        data: (sinceInceptionData as any).data[key]
          .filter((item: any) => item[0] >= startTs && item[0] <= endTs)
          .map((item: any) => [
            item[0],
            (item[1] * 100) / cal[index].data[0][1],
          ]),
        name: key,
        marker: { enabled: false },
        color: index === 0 ? "var(--primary-900)" : "#63a387",
        borderColor: index === 0 ? "var(--primary-900)" : "#63a387",
      }),
    );

    return result;
  };

  // 4. transformedData mein custom case add karo
  const transformedData =
    dateRange === "custom"
      ? getCustomFilteredSeries()
      : chartData
          ?.filter((dataItem) => dataItem.name === dateRange)
          .map((data: any) => {
            return Object.keys(data?.data).map((key, index) => {
              return {
                data: [
                  data.data[key].map((item: any) => [item[0], item[1]]),
                ].flat(),
                name: key,
                marker: data.marker,
                color: index === 0 ? "var(--primary-900)" : "#63a387",
                borderColor: index === 0 ? "var(--primary-900)" : "#63a387",
              };
            });
          })
          .flat();

  const options = {
    chart: {
      backgroundColor: "var(--white)",
      borderRadius: 16,
      height: 500,
    },
    title: {
      text: " ",
    },
    yAxis: {
      opposite: false,
      gridLineColor: `var(--neutral-100)`,
      color: `var(--warning-700)`,
      labels: {
        formatter(this: any) {
          return formatYAxisLabel(this?.value);
        },
        style: {
          color: "var(--primary-900)",
          fontSize: "12px",
          fontWeight: "bold",
        },
      },
    },
    xAxis: {
      type: "datetime",
      dateTimeLabelFormats: {
        day: "%e of %b, %Y",
      },
    },
    series: transformedData,
    tooltip: {
      pointFormatter(this: any) {
        return `<span style="color:${this.color}">●</span> ${
          this.series.name
        }: <b>${this.y.toFixed(2)}</b><br/>`;
      },
    },
    plotOptions: {
      series: {
        states: {
          hover: {
            enabled: false,
          },
        },
        animation: false,
      },
    },
  };

  const btn = [
    { name: "MTD", value: "monthToDate" },
    { name: "QTD", value: "quarterToDate" },
    { name: "6M", value: "6M" },
    { name: "YTD", value: "YTD" },
    { name: "3Y", value: "3Y" },
    { name: "5Y", value: "5Y" },
    { name: "ALL", value: "sinceInception" },
    { name: "Custom", value: "custom" },
  ];

  return (
    <>
      <Card>
        <Grid2 container spacing={1}>
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography
              color={"var(--primary-900)"}
              fontSize={20}
              fontWeight={700}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              Benchmark Comparison
            </Typography>
            <GKButton
              onClick={() => {
                setShowBenchmarkModal(true);
              }}
              variant="contained"
              color="primary"
            >
              Select Benchmark
            </GKButton>
          </Grid2>
          <Grid2 size={12} gap={1}>
            {btn.map((dataItem) => {
              return (
                <GKButton
                  key={dataItem.value}
                  // 6. Custom button ke liye disabled logic alag
                  disabled={
                    dataItem.value !== "custom" &&
                    isEmpty(
                      chartData?.find((data) => data?.name === dataItem.value)
                        ?.data,
                    )
                  }
                  size="extraSmall"
                  variant={
                    dateRange === dataItem.value ? "contained" : "outlined"
                  }
                  // 7. Custom click hone par popover open karo
                  onClick={(e) => {
                    if (dataItem.value === "custom") {
                      setCalendarAnchor(e.currentTarget);
                    } else {
                      setDateRange(dataItem.value);
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }
                  }}
                  sx={{ mr: 1 }}
                >
                  {dataItem.name}
                </GKButton>
              );
            })}
          </Grid2>

          {/* 8. Popover with date pickers */}
          <Popover
            open={Boolean(calendarAnchor)}
            anchorEl={calendarAnchor}
            onClose={() => setCalendarAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 2,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 280,
              },
            }}
          >
            <Typography
              fontWeight={600}
              fontSize={14}
              color="var(--primary-900)"
            >
              Select Date Range
            </Typography>
            <GKDatePicker
              inputLabel="Start Date"
              value={customStartDate ? dayjs(customStartDate) : null}
              onChange={(value) =>
                setCustomStartDate(value ? value.format("YYYY-MM-DD") : "")
              }
              disableFuture
              maxDate={customEndDate ? dayjs(customEndDate) : undefined}
            />
            <GKDatePicker
              inputLabel="End Date"
              value={customEndDate ? dayjs(customEndDate) : null}
              onChange={(value) =>
                setCustomEndDate(value ? value.format("YYYY-MM-DD") : "")
              }
              disableFuture
              minDate={customStartDate ? dayjs(customStartDate) : undefined}
            />
            <GKButton
              variant="contained"
              color="primary"
              size="small"
              disabled={!customStartDate || !customEndDate}
              onClick={() => {
                setDateRange("custom");
                setCalendarAnchor(null);
              }}
              fullWidth
            >
              Apply
            </GKButton>
          </Popover>

          <Grid2 size={12}>
            {transformedData && transformedData.length > 0 ? (
              <GKCharts chartOptions={options} />
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {dateRange === "custom"
                  ? "No data found for selected date range."
                  : "No benchmark found for selected portfolio. Please select a benchmark."}
              </Alert>
            )}
          </Grid2>
          <Grid2 size={12}>
            {basketChartTableData?.data?.performance && (
              <BenchmarkTable
                data={basketChartTableData?.data?.performance || {}}
              />
            )}
          </Grid2>
        </Grid2>
      </Card>
      {showBenchmarkModal && (
        <SelectBenchmarkModal
          open={showBenchmarkModal}
          setOpen={setShowBenchmarkModal}
        />
      )}
    </>
  );
};

export default Benchmark;
